import { crm } from "../../../theme";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FaPen, FaPlus, FaTrash, FaUsers } from "react-icons/fa";
import { TeamViewModel } from "../../../models/Team";
import { deleteTeam, getTeams } from "../../../services/teamService";
import CreateTeamModal from "./CreateTeamModal";

const TeamsManager = () => {
  const [teams, setTeams] = useState<TeamViewModel[]>([]);
  const [editing, setEditing] = useState<TeamViewModel | undefined>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const load = async () => {
    const response = await getTeams();
    setTeams(response ?? []);
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id?: string) => {
    if (!id) return;
    try {
      await deleteTeam(id);
      await load();
      toast({
        title: "Equipe removida.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao remover equipe.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      <Heading size="lg" mb="24px" fontWeight="normal">
        <Flex alignItems="center" gap="12px">
          <FaUsers color="var(--icon-color)" /> Gerenciar equipes
        </Flex>
      </Heading>

      <Text mb="24px" color={crm.textMuted}>
        Equipes definem um pacote de clientes. Usuários vinculados a uma
        equipe só acessam os processos dos clientes daquela equipe; sem
        equipe, o usuário acessa todos.
      </Text>

      <Button
        leftIcon={<FaPlus />}
        backgroundColor={crm.brand}
        color="white"
        borderRadius="10px"
        _hover={{ backgroundColor: crm.brandDark }}
        mb="24px"
        onClick={() => {
          setEditing(undefined);
          onOpen();
        }}
      >
        Nova equipe
      </Button>

      <Flex
        flexDir="column"
        gap="12px"
        backgroundColor="white"
        borderRadius="12px"
        border={`1px solid ${crm.border}`}
        boxShadow={crm.shadowSm}
        p="24px"
      >
        {teams.length === 0 && (
          <Text color={crm.textFaint}>Nenhuma equipe cadastrada ainda.</Text>
        )}
        {teams.map((t) => (
          <Flex
            key={t.id}
            alignItems="center"
            justifyContent="space-between"
            borderWidth="1px"
            borderRadius="md"
            p="12px 16px"
          >
            <Box>
              <Heading size="sm" fontWeight="semibold">
                {t.name}
              </Heading>
              <Text color={crm.textMuted} fontSize="sm">
                {t.clientsIds?.length ?? 0} cliente
                {(t.clientsIds?.length ?? 0) !== 1 ? "s" : ""}
              </Text>
            </Box>
            <Flex gap="8px">
              <IconButton
                aria-label="Editar"
                icon={<FaPen />}
                size="sm"
                variant="ghost"
                color={crm.brand}
                borderRadius="10px"
                _hover={{ bg: crm.brandSoft }}
                onClick={() => {
                  setEditing(t);
                  onOpen();
                }}
              />
              <IconButton
                aria-label="Remover"
                icon={<FaTrash />}
                size="sm"
                colorScheme="red"
                onClick={() => remove(t.id)}
              />
            </Flex>
          </Flex>
        ))}
      </Flex>

      <CreateTeamModal
        isOpen={isOpen}
        onClose={onClose}
        team={editing}
        onSaved={() => load()}
      />
    </Box>
  );
};

export default TeamsManager;
